import { Link, useParams } from "react-router-dom";
import { ChevronRight, MessageCircle, ShieldCheck, Info } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { EnquiryDialog, BulkForm } from "@/components/forms";
import ProductCard from "@/components/ProductCard";
import { useSite } from "@/context/SiteContext";
import { imgSrc, waLink } from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const { products, settings, loading } = useSite();
  const product = products.find((p) => p.slug === slug);

  if (loading) {
    return <div className="pt-44 pb-24 container-site text-brand-charcoal/50 text-sm">Loading product…</div>;
  }

  if (!product) {
    return (
      <div className="pt-44 pb-24 container-site">
        <h1 className="font-display text-3xl font-extrabold text-brand-deep">Product not found</h1>
        <Link to="/products" data-testid="product-not-found-back" className="mt-4 inline-block text-brand-gold font-bold">
          ← Back to all products
        </Link>
      </div>
    );
  }

  const others = products.filter((p) => p.slug !== slug);
  const specs = Object.entries(product.specs || {});

  return (
    <>
      <Seo
        title={`${product.name} — ${product.category} in 25 kg Pack | ICROPSIA India`}
        description={`${product.short_description} Available across Bihar through ICROPSIA's agricultural partner network.`}
      />

      <section className="pt-32 sm:pt-36 pb-16 bg-brand-beige/50">
        <div className="container-site">
          <nav data-testid="product-breadcrumbs" className="flex items-center gap-1.5 text-xs font-semibold text-brand-deep/50 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-deep transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/products" className="hover:text-brand-deep transition-colors">Products</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-brand-deep">{product.name}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="relative rounded-[2rem] overflow-hidden aspect-[4/4.4] shadow-2xl shadow-brand-deep/20">
                <img src={imgSrc(product.image)} alt={`${product.name} — ${product.category}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/50 to-transparent" />
                <span className="absolute top-5 left-5 bg-brand-gold text-brand-deep text-xs font-bold px-4 py-2 rounded-full">
                  {product.pack_size || "25 kg"} Pack
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <Eyebrow>{product.category}</Eyebrow>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-deep leading-[1.05]">
                {product.name}
              </h1>
              <p className="mt-5 text-brand-charcoal/70 leading-relaxed text-base sm:text-lg">{product.description || product.short_description}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <EnquiryDialog
                  productName={product.name}
                  triggerTestid="product-enquire-cta"
                  triggerClassName="rounded-full bg-brand-deep px-7 py-4 text-sm font-bold text-white transition-all hover:bg-brand-forest shadow-lg shadow-brand-deep/25"
                >
                  Enquire About This Product
                </EnquiryDialog>
                <a
                  href={waLink(settings, `Hello ICROPSIA team, I am interested in ${product.name}. Please share product and availability details.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-whatsapp-cta"
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-sm font-bold text-white transition-all hover:brightness-110 shadow-lg shadow-[#25D366]/25"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp Enquiry
                </a>
              </div>
              <p className="mt-6 flex items-start gap-2 text-xs text-brand-charcoal/50 leading-relaxed">
                <ShieldCheck className="w-4 h-4 text-brand-gold shrink-0 mt-0.5" />
                Supplied in professional {product.pack_size || "25 kg"} branded packaging for agricultural markets across {product.region || "Bihar"}.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-site">
          <Reveal>
            <Eyebrow>Product Information</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-deep">
              Specifications for {product.name}
            </h2>
          </Reveal>
          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {specs.map(([key, value], i) => {
              const pending = typeof value === "string" && value.includes("[INSERT");
              return (
                <Reveal key={key} delay={i * 0.04}>
                  <div data-testid={`spec-card-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="h-full rounded-2xl border border-brand-deep/10 bg-white p-6">
                    <dt className="text-xs font-bold uppercase tracking-wider text-brand-deep/50">{key}</dt>
                    <dd className={`mt-2 font-display font-bold ${pending ? "text-brand-charcoal/35 italic text-sm font-body font-medium flex items-start gap-1.5" : "text-brand-deep text-lg"}`}>
                      {pending && <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand-gold" />}
                      {pending ? "To be updated — verified information pending" : value}
                    </dd>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <p className="mt-6 text-xs text-brand-charcoal/50 flex items-start gap-2 max-w-2xl leading-relaxed">
              <Info className="w-4 h-4 text-brand-gold shrink-0 mt-0.5" />
              ICROPSIA publishes technical specifications only after they are verified. Fields marked as pending will be
              updated as official product documentation becomes available.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-site">
          <Reveal>
            <div className="rounded-[2rem] bg-brand-ink text-white p-7 sm:p-12 relative overflow-hidden">
              <div className="absolute inset-0 grain opacity-20" />
              <div className="relative grid lg:grid-cols-2 gap-10">
                <div>
                  <Eyebrow light>Bulk Supply</Eyebrow>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
                    Looking for Bulk Supply of {product.name}?
                  </h2>
                  <p className="mt-3 text-white/65 text-sm leading-relaxed">
                    Retailers, dealers, distributors and bulk buyers can request supply directly. Our team responds with
                    availability and commercial details.
                  </p>
                </div>
                <div className="bg-brand-ivory text-brand-charcoal rounded-3xl p-6">
                  <BulkForm />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {others.length > 0 && (
        <section className="pb-24">
          <div className="container-site">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-deep">Also in the Portfolio</h2>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-7 lg:gap-9 max-w-3xl">
              {others.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
